import { Injectable, signal } from '@angular/core';

import { StorageKey } from '../../shared/models/storage-keys';
import { WebviewOutboundEvent } from './webview.model';

declare global {
  interface Window {
    // Имена те же, что и в старом проекте (utils/mobile.ts: window.mobileApp / webkit.messageHandlers.mobileApp) —
    // это их проставляет нативная оболочка (Android addJavascriptInterface / iOS WKScriptMessageHandler).
    mobileApp?: { postMessage(message: string): void };
    webkit?: { messageHandlers?: { mobileApp?: { postMessage(message: string): void } } };
  }
}

/**
 * Мост между Angular-страницей и нативной оболочкой (Qazpost superapp), в которую это приложение
 * встраивается через WebView. Аналог isWebView-детекта и sendMobileEvent() из старого проекта.
 */
@Injectable({ providedIn: 'root' })
export class WebviewBridgeService {
  readonly isWebView = signal(false);
  readonly appVersion = signal<string | null>(null);

  constructor() {
    const params = new URLSearchParams(window.location.search);
    const appVersionParam = params.get('appVersion');
    const localeParam = params.get('locale');
    // Как и в старом проекте (AuthContext.tsx): наличие appVersion в query — уже признак
    // WebView, отдельный ?webview=1 не обязателен, но оставлен для ручного тестирования.
    const isWebViewParam =
      params.get('webview') === '1' || !!appVersionParam || !!window.mobileApp || !!window.webkit?.messageHandlers?.mobileApp;

    // Значение 'true' (не '1') и запись сразу в оба хранилища — 1:1 с AuthContext.tsx старого
    // проекта. Важно: SuperApp местами пишет эти флаги в localStorage сам, напрямую (как и
    // с accessToken/refreshToken — см. StorageKey) — с другим значением мы бы его не увидели.
    if (isWebViewParam) {
      this.isWebView.set(true);
      sessionStorage.setItem(StorageKey.IsWebview, 'true');
      localStorage.setItem(StorageKey.IsWebview, 'true');
    } else {
      this.isWebView.set(
        sessionStorage.getItem(StorageKey.IsWebview) === 'true' || localStorage.getItem(StorageKey.IsWebview) === 'true',
      );
    }

    if (appVersionParam) {
      this.appVersion.set(appVersionParam);
      localStorage.setItem(StorageKey.AppVersion, appVersionParam);
    } else {
      this.appVersion.set(localStorage.getItem(StorageKey.AppVersion));
    }

    if (localeParam) {
      params.delete('locale');
    }
  }

  /** Отправляет событие в нативную оболочку (Android/iOS bridge), если приложение открыто в WebView. */
  sendMobileEvent<TData = unknown>(event: WebviewOutboundEvent<TData>): void {
    if (!this.isWebView()) {
      return;
    }

    const message = JSON.stringify(event);

    if (window.mobileApp) {
      window.mobileApp.postMessage(message);
      return;
    }

    if (window.webkit?.messageHandlers?.mobileApp) {
      window.webkit.messageHandlers.mobileApp.postMessage(message);
    }
  }
}
